import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Copy, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function InviteResultsDialog({ result, onClose }) {
  const open = Boolean(result);
  const candidateLink = (interview_id) =>
    `${window.location.origin}/interview/session/${interview_id}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="invite-result-modal"
        className="max-h-[80vh] max-w-2xl overflow-auto"
      >
        <DialogHeader>
          <DialogTitle>Interview invitations ready</DialogTitle>
          <DialogDescription>
            Copy each candidate's unique link below and email it to them — or
            integrate Resend later for one-click sends.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-3">
          {(result?.invitations || []).map((inv, k) => (
            <li
              key={k}
              data-testid={`invitation-row-${k}`}
              className="border border-slate-200 p-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-heading text-base font-medium">
                    {inv.candidate_name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {inv.candidate_email}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  {inv.email_sent && (
                    <span
                      data-testid={`invitation-email-sent-${k}`}
                      className="inline-flex items-center gap-1 border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700"
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      Email sent
                    </span>
                  )}
                  {inv.email_error && (
                    <span
                      title={inv.email_error}
                      className="inline-flex items-center gap-1 border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      Email failed
                    </span>
                  )}
                  {inv.already_invited && (
                    <span className="border border-slate-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                      Already invited
                    </span>
                  )}
                  {inv.error && (
                    <span className="border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-red-600">
                      {inv.error}
                    </span>
                  )}
                </div>
              </div>
              {inv.interview_id && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Input
                    readOnly
                    value={candidateLink(inv.interview_id)}
                    className="flex-1 rounded-sm border-slate-300 font-mono text-xs"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`copy-invite-link-${k}`}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        candidateLink(inv.interview_id)
                      );
                      toast.success("Link copied");
                    }}
                    className="rounded-sm border-slate-300"
                  >
                    <Copy className="mr-2 h-3 w-3" />
                    Copy
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button
            variant="outline"
            data-testid="copy-all-invites-btn"
            onClick={() => {
              const text = (result?.invitations || [])
                .filter((x) => x.interview_id)
                .map(
                  (x) =>
                    `${x.candidate_name} <${x.candidate_email}> — ${candidateLink(x.interview_id)}`
                )
                .join("\n");
              navigator.clipboard.writeText(text);
              toast.success("All links copied");
            }}
            className="rounded-sm"
          >
            <Copy className="mr-2 h-3 w-3" />
            Copy all
          </Button>
          <Button
            onClick={onClose}
            className="rounded-sm bg-slate-900 hover:bg-slate-800"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
